import { ErrorCode } from '@/api/types/pt_enums'
import { useToast } from '@/hook/common/useToast'
import router from '@/router'
import { usePlatformStore } from '@/stores/platform'
import { PtError } from '@/types/pt_types'
import { appType, clientType, env } from '@/utils/env'
import * as logUtil from '@/utils/log'
import { getAccessToken, getLoginInfo, LOGIN_PAGE, setLoginInfo } from '@/utils/platform'

import { apiClient } from '../http_client'

import type { IInterceptor } from '@/axios/IInterceptor'
import type { ResponseModel } from '@/types/pt_types'
import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios'

const toast = useToast()
// 使用箭头函数避免this丢失问题
class ApiInterceptor implements IInterceptor {
  /**
   * 请求发出之前处理请求配置
   * @param config 请求配置
   */
  beforeRequest = async (config: InternalAxiosRequestConfig<any>): Promise<InternalAxiosRequestConfig<any>> => {
    config.headers.set('X-App-Type', appType)
    config.headers.set('X-Client-Type', clientType.value)
    config.headers.set('X-Server-Token', env.PT_SERVER_TOKEN)
    if (config.checkToken === true || config.checkToken === undefined || config.checkToken === null) {
      const accessToken = await getAccessToken()
      config.headers.set('Authorization', accessToken || '')
    } else if (config.checkToken === 'MAYBE') {
      // 不尝试获取，有就传，没有不传
      const loginInfo = getLoginInfo()
      config.headers.set('Authorization', loginInfo.accessToken || '')
    }
    return config
  }

  /**
   * 处理请求错误
   * @param error 请求错误信息
   */
  handleRequestError = (error: any): any => {
    logUtil.error(`handle request error: ${JSON.stringify(error)}`)
    return Promise.reject(new PtError((error as any).code || 499, '请求失败'))
  }

  /**
   * 服务器响应请求成功
   * @param response 响应体(不要返回其它类型避免影响其它拦截器)
   */
  onResponseSucceed = async (response: AxiosResponse): Promise<AxiosResponse> => {
    if (response.status !== 200) {
      return Promise.reject(new PtError(ErrorCode.SVR_ERR.value, response.statusText ?? '请求错误'))
    }
    const respModel: ResponseModel<any> = response.data
    if (respModel && respModel.code !== 0) {
      // 业务异常
      if (respModel.code === ErrorCode.ACCESS_TIMEOUT.value) {
        // 访问令牌过期了, 尝试获取新令牌后重试
        const originalRequest = response.config
        if (originalRequest._retry) {
          // 避免拦截器无限循环
          return Promise.reject(new PtError(respModel.code!, respModel.message!))
        }
        originalRequest._retry = true
        const loginInfo = { ...getLoginInfo(), accessExpireTime: 0 }
        setLoginInfo(loginInfo)
        const platformStore = usePlatformStore()
        platformStore.loginInfo = loginInfo
        const accessToken = await getAccessToken(true, originalRequest.apiOptions?.redirectToLogin !== false)
        originalRequest.headers.set('Authorization', accessToken)
        const newResp = await apiClient.instance.request(originalRequest)
        return newResp
      } else if (respModel.code === ErrorCode.NOT_LOGIN.value) {
        const platformStore = usePlatformStore()
        platformStore.logout()
        if (response.config.apiOptions?.redirectToLogin !== false) {
          // 没有设置默认是要跳转的
          const currentRoute = router.currentRoute.value
          if (currentRoute.path !== LOGIN_PAGE) {
            let fullPath = currentRoute.path
            if (Object.keys(currentRoute.query).length) {
              fullPath += `?${Object.keys(currentRoute.query)
                .map((key) => `${key}=${currentRoute.query[key]}`)
                .join('&')}`
            }
            const redirectUrl = `${LOGIN_PAGE}?redirect=${encodeURIComponent(fullPath)}`
            router.push(redirectUrl)
          }
        }
      }
      if (response.config.apiOptions?.silent !== true) {
        toast.error(respModel.message!)
      }
      return Promise.reject(new PtError(respModel.code!, respModel.message!))
    }
    return response
  }

  /**
   * 服务器响应请求失败
   * @param error 响应错误信息
   */
  onResponseFailed = (error: any): any => {
    logUtil.error(`on response failed: ${JSON.stringify(error)}`)
    return Promise.reject(new PtError((error as any).code || 599, '响应异常'))
  }
}

export default ApiInterceptor
